import type { IconName, ReadMoreProps, SwitchProps } from '@lemonade-hq/uniclient-theme';
import type { UniclientAction } from '../action';
import type { GridResponsiveColumns } from '../utils';
import type { ContentItemProps } from './content';
import type { FormSwitchElementConfigurableProps } from './form/composers';
import type { FormProps } from './form/form';
import type { FormElementProps } from './form/form.types';
import type { InterpolableTextProps } from './interpolable_text';
import type { TagComposerProps } from './tag';
import type { UniclientBaseComponentProps, UniclientWizardComponentBase } from './types';

export interface ListProps<TAttribute extends string, TContent extends string = string>
  extends UniclientBaseComponentProps {
  readonly showSeparators?: boolean;
  readonly items: ListItemProps<TAttribute, TContent>[];
  readonly collapseOptions?: ReadMoreProps;
  readonly columns?: GridResponsiveColumns;
  readonly wrapWithCard?: boolean;
}

export type ListItemContentType = ContentItemProps[] | InterpolableTextProps<string>;

export type ListItemThumbnailSize = 'md' | 'sm';

export type InputSize = 'full' | 'lg' | 'md' | 'sm';

export type TooltipIcon = { readonly icon: IconName; readonly text: string };

export interface BaseListItem<TType extends string, TAttribute extends string, TContent extends string = string> {
  readonly type: TType;
  readonly thumbnail?: string;
  readonly thumbnailSize?: ListItemThumbnailSize;
  readonly title: string | { readonly text: string; readonly tooltip: TooltipIcon };
  readonly subtitle?: InterpolableTextProps<TContent>;
  readonly content?: ListItemContentType;
  readonly bottomInputSize?: InputSize;
  readonly bottomInput?: FormElementProps<TAttribute> | FormProps<TAttribute>;
  readonly expanded?: boolean;
  readonly disabled?: boolean;
  readonly sideText?: string;
  readonly disableEntireItem?: boolean;
}

export type StandardListItemProps<TAttribute extends string, TContent extends string = string> = BaseListItem<
  'standard',
  TAttribute,
  TContent
> & {
  readonly sideElementSize?: InputSize;
  readonly sideElementCenterAlign?: boolean;
  readonly sideElement?:
    | Exclude<FormElementProps<TAttribute>, FormSwitchElementConfigurableProps<TAttribute>>
    | TagComposerProps;
};

export type SwitchListItemProps<TAttribute extends string, TContent extends string = string> = BaseListItem<
  'switch',
  TAttribute,
  TContent
> & {
  readonly selectedThumbnail?: string;
  readonly attribute: TAttribute;
  readonly onAction?: UniclientAction;
  readonly offAction?: UniclientAction;
  readonly variant?: SwitchProps['variant'];
  readonly alwaysShowBottomInput?: boolean;
  readonly disabled?: boolean;
  /**
   * Will be shown only if the switch is disabled
   */
  readonly tooltip?: string;
};

export type ListItemProps<TAttribute extends string, TContent extends string = string> =
  | StandardListItemProps<TAttribute, TContent>
  | SwitchListItemProps<TAttribute, TContent>;

export type UniclientListProps<TAttribute extends string = string, TContent extends string = string> = ListProps<
  TAttribute,
  TContent
> &
  UniclientWizardComponentBase<'list'>;

// TODO: this type isn't strict enough, find a way to restrict the passed props to the correct type like in form
export function list<TAttribute extends string, const TContent extends string>(
  props: ListProps<TAttribute, TContent>,
): UniclientListProps<TAttribute, TContent> {
  return { ...props, type: 'list' };
}

export function listItem<
  TAttribute extends string,
  const TContent extends string,
  TItem extends Omit<StandardListItemProps<TAttribute, TContent>, 'type'>,
>(props: TItem): TItem & { readonly type: 'standard' } {
  return { ...props, type: 'standard' };
}

export function listSwitchItem<
  TAttribute extends string,
  const TContent extends string,
  TItem extends Omit<SwitchListItemProps<TAttribute, TContent>, 'type'>,
>(props: TItem): TItem & { readonly type: 'switch' } {
  return { ...props, type: 'switch' };
}
